import React from 'react'
import {
  useGetFeatureFlagsQuery,
  useUpdateFeatureFlagMutation,
  useGetIntegrationStatsQuery,
  useGetIntegrationsQuery,
  useGetSourceSelectionStatsQuery,
  useGetMoSourceConnectionsQuery,
} from '../store/api/integrationsApi'
import toast from 'react-hot-toast'
import { formatDateTimeEST } from '../utils/dateUtils'

const SOURCE_LABELS: Record<string, string> = {
  slack: 'Slack',
  jira: 'Jira',
  azure_devops: 'Azure DevOps',
  bitbucket: 'Bitbucket',
  linear: 'Linear',
  github: 'GitHub',
  gitlab: 'GitLab',
}

const SOURCE_COLORS: Record<string, string> = {
  slack: 'bg-purple-100 text-purple-800',
  jira: 'bg-blue-100 text-blue-800',
  azure_devops: 'bg-sky-100 text-sky-800',
  bitbucket: 'bg-blue-100 text-blue-800',
  linear: 'bg-violet-100 text-violet-800',
  github: 'bg-gray-800 text-white',
  gitlab: 'bg-orange-100 text-orange-800',
}

const Integrations: React.FC = () => {
  const { data: flags, isLoading: flagsLoading } = useGetFeatureFlagsQuery()
  const { data: stats, isLoading: statsLoading } = useGetIntegrationStatsQuery()
  const { data: integrations, isLoading: integrationsLoading } = useGetIntegrationsQuery()
  const { data: sourceStats, isLoading: sourceStatsLoading } = useGetSourceSelectionStatsQuery()
  const { data: moConnections, isLoading: moConnectionsLoading } = useGetMoSourceConnectionsQuery()
  const [updateFlag] = useUpdateFeatureFlagMutation()

  const handleToggleFlag = async (key: string, currentValue: boolean) => {
    try {
      await updateFlag({ key, value: !currentValue }).unwrap()
      toast.success(`${key} ${!currentValue ? 'enabled' : 'disabled'}`)
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update feature flag')
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      pending_setup: 'bg-yellow-100 text-yellow-800',
      disconnected: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      importing: 'bg-blue-100 text-blue-800',
      started: 'bg-blue-100 text-blue-800',
      processing: 'bg-blue-100 text-blue-800',
    }
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace(/_/g, ' ')}
      </span>
    )
  }

  const getTypeBadge = (type: string) => {
    const styles: Record<string, string> = {
      jira: 'bg-blue-100 text-blue-800',
      asana: 'bg-orange-100 text-orange-800',
      trello: 'bg-sky-100 text-sky-800',
      linear: 'bg-purple-100 text-purple-800',
      github: 'bg-gray-800 text-white',
    }
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
        <p className="text-gray-600">Manage third-party integration feature flags and monitor active integrations</p>
      </div>

      {/* Feature Flags */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Feature Flags</h2>
          <p className="text-sm text-gray-500 mt-1">Control which integrations are available to users</p>
        </div>
        <div className="p-6">
          {flagsLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-12 bg-gray-200 rounded"></div>
              <div className="h-12 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {flags?.map((flag) => (
                <div key={flag.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900">{flag.key}</span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                        flag.value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {flag.value ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                  </div>
                  <button
                    onClick={() => handleToggleFlag(flag.key, flag.value as boolean)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      flag.value ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        flag.value ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}

              {(!flags || flags.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <p>No feature flags found. Run the integration migration to create them.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="text-sm text-gray-500">Total Integrations</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">
            {statsLoading ? '...' : stats?.totalIntegrations || 0}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="text-sm text-gray-500">Active Integrations</div>
          <div className="text-3xl font-bold text-green-600 mt-1">
            {statsLoading ? '...' : stats?.activeIntegrations || 0}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="text-sm text-gray-500">Imported Projects</div>
          <div className="text-3xl font-bold text-blue-600 mt-1">
            {statsLoading ? '...' : stats?.totalImportedProjects || 0}
          </div>
        </div>
      </div>

      {/* Active Integrations Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Active Integrations</h2>
        </div>
        <div className="overflow-x-auto">
          {integrationsLoading ? (
            <div className="p-6 animate-pulse space-y-4">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          ) : integrations && integrations.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projects</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Members</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {integrations.map((integration) => (
                  <tr key={integration.uuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {integration.organization_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getTypeBadge(integration.integration_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {integration.integration_method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(integration.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {integration.project_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {integration.member_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {integration.last_sync_at
                        ? formatDateTimeEST(integration.last_sync_at)
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No integrations yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Integrations will appear here when users connect their Jira (or other tools).
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Imports */}
      {stats?.recentImports && stats.recentImports.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Imports</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Found</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imported</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.recentImports.map((imp) => (
                  <tr key={imp.uuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{imp.project_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{getTypeBadge(imp.integration_type)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{getStatusBadge(imp.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{imp.records_found}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{imp.records_imported}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">{imp.records_failed}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {imp.started_at ? formatDateTimeEST(imp.started_at) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Mo Source Selection Stats (Onboarding) */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Mo Onboarding — Source Selections</h2>
          <p className="text-sm text-gray-500 mt-1">Which tools people are choosing during Mo setup</p>
        </div>
        <div className="p-6">
          {sourceStatsLoading ? (
            <div className="animate-pulse h-32 bg-gray-200 rounded"></div>
          ) : sourceStats?.bySource && sourceStats.bySource.length > 0 ? (
            <>
              <div className="mb-4">
                <span className="text-sm text-gray-500">Total users who selected sources: </span>
                <span className="text-lg font-bold text-gray-900">{sourceStats.totalUsers}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Users</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sourceStats.bySource.map((row: any, i: number) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${SOURCE_COLORS[row.source_key] || 'bg-gray-100 text-gray-800'}`}>
                            {SOURCE_LABELS[row.source_key] || row.source_key}
                          </span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 capitalize">{row.selection_type}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{row.total}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{row.unique_users}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No source selections recorded yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Mo Source Connections (Azure DevOps, Bitbucket, Linear) */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Mo Source Connections</h2>
          <p className="text-sm text-gray-500 mt-1">Azure DevOps, Bitbucket, and Linear connections across organizations</p>
        </div>
        <div className="overflow-x-auto">
          {moConnectionsLoading ? (
            <div className="p-6 animate-pulse space-y-4">
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          ) : moConnections && moConnections.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Connected By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {moConnections.map((conn: any) => (
                  <tr key={conn.uuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {conn.organization_name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${SOURCE_COLORS[conn.source_type] || 'bg-gray-100 text-gray-800'}`}>
                        {SOURCE_LABELS[conn.source_type] || conn.source_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {conn.site_name || conn.site_url || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(conn.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {conn.installed_by_email || conn.external_user_name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {conn.created_at ? formatDateTimeEST(conn.created_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-sm">No Mo source connections yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Integrations
